import { useNavigate } from "react-router-dom";
import { Pencil, Ban, MapPin, CalendarDays, Info, ArrowUpRight } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { CORES_MODULO } from "@/modules/calendario/cores";
import { rotuloDiaLongo, formatarHora, chaveDia } from "@/shared/lib/datas";
import type { ItemCalendario } from "@/modules/calendario/services/calendarioService";

interface DetalheItemCalendarioProps {
  item: ItemCalendario | null;
  /** Se a pessoa pode editar/cancelar este item (líder do ministério ou gestor do módulo). Não usado para Escala — ver comentário abaixo. */
  podeGerir: boolean;
  onClose: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  pendente: "Pendente",
  confirmada: "Confirmada",
  agendado: "Confirmado",
  ativa: "Ativa",
  cancelada: "Cancelada",
  cancelado: "Cancelado",
  recusada: "Recusada",
  recusado: "Recusado",
};
const STATUS_COR: Record<string, string> = {
  pendente: "bg-warning/15 text-warning",
  confirmada: "bg-success/10 text-success",
  agendado: "bg-primary/10 text-primary",
  ativa: "bg-success/10 text-success",
  cancelada: "bg-surface-2 text-muted",
  cancelado: "bg-surface-2 text-muted",
  recusada: "bg-danger/12 text-danger",
  recusado: "bg-danger/12 text-danger",
};

/**
 * Detalhe de um item do Calendário Central com as ações de editar/cancelar
 * (Sprint 3). As ações reaproveitam as telas de Eventos/Reservas: navegam para
 * o módulo com o item já aberto, para não duplicar formulário nem regra de
 * permissão. Biblioteca é só leitura aqui.
 *
 * Escala (Fase 3, 27/09/2026 — decisão registrada na seção 21 do relatório
 * técnico): sempre somente leitura AQUI, com um link "Ver na tela de
 * Escalas". A autorização de editar/cancelar uma escala é POR MINISTÉRIO
 * DONO (`lidera_ou_gerencia_ministerio`), não o `podeGerir` genérico único
 * que esta tela calcula uma vez por carregamento — forçar Escala nesse
 * booleano reproduziria exatamente o erro que a governança do projeto pede
 * para evitar (duplicar/gambiarrar autorização no cliente). A tela real de
 * Escala (`/escalas/:id`) já aplica a autorização correta, por item.
 */
export function DetalheItemCalendario({ item, podeGerir, onClose }: DetalheItemCalendarioProps) {
  const navigate = useNavigate();
  if (!item) return null;

  const cor = CORES_MODULO[item.modulo];
  const multiDia = !!item.fim && chaveDia(item.fim) !== chaveDia(item.inicio);
  const editavel = podeGerir && (item.modulo === "evento" || item.modulo === "reserva");

  function editar() {
    if (!item) return;
    const rota = item.modulo === "evento" ? "eventos" : "reservas";
    navigate(`/${rota}?editar=${item.refId}`);
    onClose();
  }
  function cancelar() {
    if (!item) return;
    // Reteste 22-23/07/2026: evento e reserva agora "cancelam" (com motivo) —
    // cada módulo abre a confirmação pelo query-param ?cancelar=.
    const alvo = `/${item.modulo === "evento" ? "eventos" : "reservas"}?cancelar=${item.refId}`;
    navigate(alvo);
    onClose();
  }
  function verNaTelaDeEscala() {
    if (!item) return;
    navigate(`/escalas/${item.refId}`);
    onClose();
  }

  return (
    <Modal titulo={item.titulo} aberto={!!item} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${cor.tag}`}>{cor.rotulo}</span>
          {item.status && (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                STATUS_COR[item.status] ?? "bg-surface-2 text-muted"
              }`}
            >
              {STATUS_LABEL[item.status] ?? item.status}
            </span>
          )}
          {item.visibilidade === "privado" && (
            <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-bold text-muted">
              Privado
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1 text-sm text-text">
          <p className="flex items-center gap-1.5">
            <CalendarDays size={14} className="shrink-0 text-muted" />
            {rotuloDiaLongo(item.inicio)}
            {multiDia && ` – ${rotuloDiaLongo(item.fim!)}`}
            {!item.diaInteiro && ` · ${formatarHora(item.inicio)}`}
            {!item.diaInteiro && item.fim && !multiDia && `–${formatarHora(item.fim)}`}
          </p>
          {item.local && (
            <p className="flex items-center gap-1.5">
              <MapPin size={14} className="shrink-0 text-muted" />
              {item.local}
            </p>
          )}
          <p className="text-xs text-muted">{item.ministerio ?? "Administração"}</p>
        </div>

        {item.observacao && (
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted">
              {item.modulo === "reserva" ? "Observações" : "Descrição"}
            </span>
            <p className="whitespace-pre-wrap break-words rounded-lg bg-surface-2 px-3 py-2 text-sm leading-relaxed text-text">
              {item.observacao}
            </p>
          </div>
        )}

        {editavel ? (
          <>
            <div className="flex gap-2">
              <button
                onClick={editar}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-on-primary transition-colors hover:opacity-90"
              >
                <Pencil size={16} /> Editar
              </button>
              <button
                onClick={cancelar}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-danger/40 px-3 py-2.5 text-sm font-semibold text-danger transition-colors hover:bg-danger/10"
              >
                <Ban size={16} /> {item.modulo === "evento" ? "Cancelar evento" : "Cancelar reserva"}
              </button>
            </div>
            {item.modulo === "reserva" && (
              <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted">
                <Info size={13} className="mt-px shrink-0 text-primary" />
                Mudar ambiente, data ou horário reenvia a reserva para aprovação do ministério
                responsável (volta para “Pendente”), se você não gerencia o elo.
              </p>
            )}
          </>
        ) : item.modulo === "escala" ? (
          <button
            onClick={verNaTelaDeEscala}
            className="flex items-center justify-center gap-2 rounded-lg border border-success/40 px-3 py-2.5 text-sm font-semibold text-success transition-colors hover:bg-success/10"
          >
            Ver na Escala <ArrowUpRight size={16} />
          </button>
        ) : (
          <p className="text-xs italic text-muted">
            {item.modulo === "biblioteca"
              ? "Devolução prevista da Biblioteca."
              : "Você não gerencia este item — apenas visualização."}
          </p>
        )}
      </div>
    </Modal>
  );
}
